import type {Result} from 'framework';
import type {ImageCodec, ImageCodecCapabilities} from '../core/image-codec';
import {probeCodecCapabilities} from '../core/orientation-probe';
import {normalizePreprocessOptions, runPreprocessPipeline} from '../core/preprocess-pipeline';
import type {ImagePreprocessError} from '../image-preprocess.error';
import type {Dimensions, PreparedImage, PreprocessImageOptions} from '../types';
import type {ImagePreprocessor} from './image-preprocessor';
import {SerialQueue} from './serial-queue';

/** Runs the pipeline where it is called; used when there is no Worker or as the demotion target. */
export class MainThreadImagePreprocessor<TImage extends Dimensions> implements ImagePreprocessor {
  private capabilities: Promise<ImageCodecCapabilities> | null = null;
  private readonly queue = new SerialQueue();

  constructor(private readonly codec: ImageCodec<TImage>) {}

  preprocess(
    file: File,
    options: PreprocessImageOptions,
  ): Promise<Result<PreparedImage, ImagePreprocessError>> {
    return this.queue.run(async () =>
      runPreprocessPipeline(
        file,
        normalizePreprocessOptions(options),
        this.codec,
        await this.codecCapabilities(),
      ),
    );
  }

  private codecCapabilities(): Promise<ImageCodecCapabilities> {
    this.capabilities ??= probeCodecCapabilities(this.codec);
    return this.capabilities;
  }
}
